import 'react-native-get-random-values';
import 'react-native-url-polyfill/auto';

import {Bucket, UploadingStatus} from './type';
import {DeleteUploadedImageData, UpdateUploadingStatus} from './hook';
import {
  PutObjectCommand,
  PutObjectCommandInput,
  S3Client,
} from '@aws-sdk/client-s3';
import {
  RunReceiptPrefixURL,
  awsRegion,
  upcImagePrefixURL,
} from '../utils/constants';
import {getConfig, nothing} from '@buncha/utils/appInfo';

import {Analytics} from '../analytics/analytics';
import {Config} from '@buncha/utils/types';
import {fromCognitoIdentityPool} from '@aws-sdk/credential-providers';
import {getSignedUrl} from '@aws-sdk/s3-request-presigner';

class FileUploadService {
  private readonly client: S3Client;

  constructor() {
    this.client = new S3Client({
      region: awsRegion,
      credentials: fromCognitoIdentityPool({
        identityPoolId: nothing(getConfig(Config.awsIncognitoIdentityPoolId)),
        clientConfig: {region: awsRegion},
      }),
    });
  }

  async uploadService(
    params: PutObjectCommandInput,
    runId: number,
    fileName: string,
  ) {
    try {
      const command = new PutObjectCommand({
        Bucket: params.Bucket,
        Key: params.Key,
        ContentType: 'image/jpg',
      });
      const url = await getSignedUrl(this.client, command, {
        expiresIn: 3600,
      });

      const xhr = new XMLHttpRequest();
      await new Promise((resolve, _reject) => {
        xhr.upload.addEventListener('progress', event => {
          if (event.lengthComputable) {
            const loaded = event?.loaded || 0;
            const total = event?.total || 1;
            const percentage =
              total > 0 ? Number(((loaded / total) * 100).toFixed(2)) : 0;

            if (loaded < total)
              UpdateUploadingStatus(
                runId,
                fileName,
                UploadingStatus.inprogress,
                percentage,
              );
            else if (loaded === total) DeleteUploadedImageData(runId, fileName);
          }
        });
        xhr.addEventListener('loadend', () => {
          resolve(xhr.readyState === 4 && xhr.status === 200);
        });
        xhr.open('PUT', url, true);
        xhr.setRequestHeader('Content-Type', 'application/octet-stream');
        xhr.send(params.Body);
      });
    } catch (error: any) {
      Analytics.eventWithProps('error', {
        type: 'Error',
        error: 'Failed to upload markup image',
        errorType: error?.message,
        errorInfo: JSON.stringify(error),
      });
      await UpdateUploadingStatus(runId, fileName, UploadingStatus.failed, 0);
    }
  }

  uploadUpcImages(
    fileName: string,
    blob: Blob,
    storeIndex: string,
    runId: number,
  ) {
    try {
      const params: PutObjectCommandInput = {
        Bucket: Bucket.upcImages,
        Key: storeIndex + '/' + fileName,
        Body: blob,
      };

      this.uploadService(params, runId, fileName);
      const url = `${upcImagePrefixURL}/${
        storeIndex + '/' + encodeURIComponent(fileName)
      }`;
      return url;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error in uploadUpcImages for ${fileName}:`, error);
    }
  }

  uploadRunReceipt(fileName: string, blob: Blob, runId: number) {
    try {
      const params = {
        Bucket: Bucket.orderImage,
        Key: 'run-receipts' + '/' + fileName,
        Body: blob,
      };

      this.uploadService(params, runId, fileName);
      const url = `${RunReceiptPrefixURL}/${
        'run-receipts' + '/' + encodeURIComponent(fileName)
      }`;
      return url;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error in uploadRunReceipt for ${fileName}:`, error);
    }
  }

  uploadMarkupTagImages(fileName: string, photo: any, runId: number) {
    if (!photo) return;
    try {
      const params = {
        Bucket: Bucket.orderImage,
        Key: 'IAMBARCODEIMG' + fileName,
        Body: photo,
      };
      this.uploadService(params, runId, fileName);

      const url = `${RunReceiptPrefixURL}/${
        'IAMBARCODEIMG' + encodeURIComponent(fileName)
      }`;
      return url;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error in uploadRunReceipt for ${fileName}:`, error);
    }
  }
}

export default FileUploadService;
